import { useState, useEffect, useCallback } from 'react';

interface PWAState {
  installed: boolean;
  installPrompt: Event | null;
  canInstall: boolean;
  offline: boolean;
}

export function usePWA() {
  const [state, setState] = useState<PWAState>({
    installed: false,
    installPrompt: null,
    canInstall: false,
    offline: !navigator.onLine,
  });

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as Record<string, boolean>).standalone === true;

    setState((prev) => ({ ...prev, installed: isStandalone }));

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setState((prev) => ({ ...prev, installPrompt: e, canInstall: true }));
    };

    const handleOnline = () => setState((prev) => ({ ...prev, offline: false }));
    const handleOffline = () => setState((prev) => ({ ...prev, offline: true }));
    const handleAppInstalled = () => {
      setState((prev) => ({ ...prev, installed: true, canInstall: false }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!state.installPrompt || !(state.installPrompt as unknown as { prompt: () => Promise<void> }).prompt) return;
    try {
      await (state.installPrompt as unknown as { prompt: () => Promise<void> }).prompt();
      setState((prev) => ({ ...prev, installPrompt: null, canInstall: false }));
    } catch {
      /* user dismissed */ }
  }, [state.installPrompt]);

  const dismissInstall = useCallback(() => {
    setState((prev) => ({ ...prev, canInstall: false }));
  }, []);

  return { ...state, promptInstall, dismissInstall };
}
